import {
  useCreateBudgetaryMutation,
  usePlaceAnOrder,
  useSaveQuoteData,
} from "@/common/api/mutation";
import { useModal } from "@/common/contexts/Modal.context";
import { usePermissions } from "@/common/contexts/Permissions.context";
import { useQuote } from "@/common/contexts/QuoteResults.context";
import { queryClient } from "@/common/utils";
import {
  DotsIcon,
  EnvelopeIcon,
  EyeIcon,
  PenIcon,
  QuotesIcon,
} from "@/components/icon";
import { EnquireQuoteModal, WinPriceModal } from "@/components/Modals";
import Typography from "@/components/Typography/Typography";
import { Button, message, Popover } from "antd";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { toast } from "react-toastify";

export default function QuoteActions({ quoteLine }: { quoteLine: any, quoteId?: number}) {
  const {isHavePermission} = usePermissions()
  const navigate = useNavigate();
  const quoteId = useParams().quoteResultId;
  const { openModal } = useModal();
  const [isOpen, setIsOpen] = useState(false);
  const { quoteState } = useQuote();
  const closePopover = () => setIsOpen(false);
  const { mutate } = useSaveQuoteData();
  const { mutate: placeOrder } = usePlaceAnOrder();
  const { mutate: saveBudgetary } = useCreateBudgetaryMutation();

  const requestFirmOffer = () => {
    saveBudgetary(
      {
        quoteLineId: quoteLine.id,
        vendorId: quoteLine.price.companyId || 0,
        quoteReqId: quoteId || 0,
      },
      {
        onSuccess: () => {
          return toast.success("Request sent");
        },
      }
    );
  };
  const actionsConfig = [
    {
      icon: <EyeIcon />,
      label: "view",
      action: () => {
        if (!quoteState) return;
        const quoteData =quoteState?.find(el=>el.generalQuoteData.quoteId === quoteLine.quoteReqId);
        if(!quoteData) return;
        closePopover();
        navigate(`/quoteLine/${quoteLine.quoteReqId}/${quoteLine.id}`, {
          state: {
            quoteLine,
            pickedAddress: {
              pickedAAddress: quoteData.pickedAAddress,
              pickedBAddress: quoteData.pickedBAddress,
            },
            generalQuoteData: quoteData.generalQuoteData,
            productName: quoteData.productType,
          },
        });
      },
    },
    {
      icon: (
        <span>
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#clip0_1493_3534)">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M10.7019 0V5.09804H15.7999V17.3333H1.52539V0H10.7019ZM8.66366 12.2353H4.58421V13.2559H8.66366V12.2353ZM11.7215 10.1961H4.58421V11.2157H11.7215V10.1961ZM10.7019 8.15686H4.58421V9.17647H10.7019V8.15686ZM12.7411 6.11765H4.58421V7.13725H12.7411V6.11765ZM8.66366 4.07945H4.58421V5.09906H8.66366V4.07945Z"
                fill="#0F0F0F"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M15.0548 3.12042C15.3291 3.39368 15.5228 3.72199 15.6472 4.07885H11.7207V0.152344C12.0776 0.276737 12.4059 0.471476 12.6802 0.744738L15.0548 3.12042Z"
                fill="#0F0F0F"
              />
            </g>
            <defs>
              <clipPath id="clip0_1493_3534">
                <rect width="17.3333" height="17.3333" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </span>
      ),
      label: "Request Firm Offer",
      action: () => {
        return requestFirmOffer();
      },
    },
    {
      icon: <EnvelopeIcon />,
      label: "Enquire",
      action: () => {
        closePopover();
        openModal(<EnquireQuoteModal data={quoteLine} />);
      },
    },
    {
      icon: <QuotesIcon />,
      label: "Win Price",
      action: () => {
        closePopover();
        openModal(<WinPriceModal data={quoteLine} />);
      },
    },
    {
      icon: <PenIcon />,
      label: "Place an order",
      action: () => {
        if (!quoteState || !quoteId)
          return message.error("Error during order placing");
        mutate(
          [
            {
              quoteLines: [
                {
                  quoteLineId: quoteLine.id,
                  vendorId: quoteLine.price.companyId,
                },
              ],
              quoteReqId: Number(quoteId),
            },
          ],
          {
            onSuccess: (data: any) => {
              if (data.quotes.length === 0 || data.lines.length === 0)
                return message.error("Error during Order placing");
              placeOrder(
                {
                  quoteId: data.quotes[0].id,
                  quoteLineId: data.lines[0].id,
                },
                {
                  onSuccess: () => {
                    queryClient.invalidateQueries();
                    toast.success("Order placed");
                  },
                }
              );
            },
          }
        );
      },
    },
  ];
  if(!isHavePermission("quotes_customer_create")){
    return null
  }
  return (
    <Popover
      open={isOpen}
      onOpenChange={setIsOpen}
      placement="bottomLeft"
      trigger="click"
      destroyTooltipOnHide
      content={
        <div className="flex flex-col items-start justify-start">
          {actionsConfig.map((el) => {
            if (
              el.label === "Place an order" &&
              quoteLine.quoteType !== "Firm"
            ) {
              return null;
            }
            if (
              el.label === "Request Firm Offer" &&
              quoteLine.quoteType !== "Budgetary"
            ) {
              return null;
            }
            if (el.label !== "view") {
              return (
                <Button
                  key={el.label}
                  type="text"
                  onClick={el.action}
                  className="flex gap-2 justify-start h-10 w-full text-start"
                >
                  {el.icon}
                  <Typography type="span">{el.label}</Typography>
                </Button>
              );
            }
            if (quoteLine.quoteType === "Firm") {
              return (
                <Button
                  key={el.label}
                  type="text"
                  onClick={el.action}
                  className="flex gap-2 justify-start h-10 w-full text-start"
                >
                  {el.icon}
                  <Typography type="span">View more</Typography>
                </Button>
              );
            }
            return (
              <Button
                key={el.label}
                type="text"
                onClick={el.action}
                className="flex gap-2 justify-start h-10 w-full text-start"
              >
                {el.icon}
                <Typography type="span">View more</Typography>
              </Button>
            );
          })}
        </div>
      }
    >
      <button className="hover:text-primary transition px-4">
        <DotsIcon />
      </button>
    </Popover>
  );
}
